import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Grid,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ArrowDownward,
  Inventory2,
  KeyboardArrowDown,
  KeyboardArrowUp,
  LocalShipping,
  Print,
  Refresh,
  RequestQuote,
  Warehouse,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import PageHeader from '../../components/PageHeader/PageHeader';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';
const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const unwrap = (r: any) => (r?.data && 'data' in r.data ? r.data.data : r?.data);

const STATUS_COLORS: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  planned: 'default', released: 'info', started: 'warning', in_progress: 'warning',
  completed: 'success', cancelled: 'error', on_hold: 'error',
  requested: 'info', waiting_for_stock: 'error', issued: 'warning', received: 'success',
  pending: 'default', scheduled: 'default',
};

const chip = (status?: string) => (
  <Chip size="small" label={String(status || '—').replace(/_/g, ' ').toUpperCase()}
    color={STATUS_COLORS[String(status)] || 'default'} />
);

const num = (n: any) => (n === null || n === undefined ? '—' : Number(n).toLocaleString());

/**
 * Production order detail: per-stage inputs/outputs (the routed material
 * flow, including by-products and stage chaining), material reconciliation,
 * material requests and costing — the UI surface for the modeled stage I/O.
 */
const WorkOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [wo, setWo] = useState<any>(null);
  const [route, setRoute] = useState<any>(null);
  const [cost, setCost] = useState<any>(null);
  const [materials, setMaterials] = useState<any[]>([]);
  const [mrs, setMrs] = useState<any[]>([]);
  const [openStages, setOpenStages] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const notify = (message: string, severity: 'success' | 'error' = 'success') => setSnackbar({ open: true, message, severity });

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      const woRes = unwrap(await axios.get(`${API_BASE}/work-orders/${id}`, { headers: authHeaders() }));
      setWo(woRes);
      const [costRes, matRes, mrRes] = await Promise.all([
        axios.get(`${API_BASE}/work-orders/${id}/cost-report`, { headers: authHeaders() }).catch(() => null),
        axios.get(`${API_BASE}/work-orders/material-requests/work-orders/${id}/material-summary`, { headers: authHeaders() }).catch(() => null),
        axios.get(`${API_BASE}/work-orders/material-requests?workOrderId=${id}`, { headers: authHeaders() }).catch(() => null),
      ]);
      setCost(costRes ? unwrap(costRes) : null);
      setMaterials(matRes ? unwrap(matRes) || [] : []);
      setMrs(mrRes ? unwrap(mrRes) || [] : []);
      if (woRes?.routingId) {
        const routeRes = await axios
          .get(`${API_BASE}/work-orders/process-routes/${woRes.routingId}`, { headers: authHeaders() })
          .catch(() => null);
        setRoute(routeRes ? unwrap(routeRes) : null);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load work order');
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const requestMaterial = async () => {
    setBusy('mr');
    try {
      const res = unwrap(await axios.post(`${API_BASE}/work-orders/material-requests`, {
        workOrderId: id,
        requestedBy: localStorage.getItem('userEmail') || 'production',
      }, { headers: authHeaders() }));
      notify(`Material request ${res.mrNumber} created (${res.lines?.length || 0} lines)`);
      await load();
    } catch (err: any) {
      notify(err?.response?.data?.message || 'Material request failed', 'error');
    } finally { setBusy(null); }
  };

  const mrAction = async (mrId: string, action: 'issue' | 'receive') => {
    setBusy(mrId + action);
    try {
      await axios.post(`${API_BASE}/work-orders/material-requests/${mrId}/${action}`, {
        [action === 'issue' ? 'issuedBy' : 'receivedBy']: localStorage.getItem('userEmail') || 'stores',
      }, { headers: authHeaders() });
      notify(`Material request ${action}d`);
      await load();
    } catch (err: any) {
      notify(err?.response?.data?.message || `${action} failed`, 'error');
    } finally { setBusy(null); }
  };

  const routeOpBySeq = new Map<number, any>(
    (route?.operations || []).map((o: any) => [o.sequence, o]),
  );
  const operations = (wo?.operations || []).slice().sort((a: any, b: any) => a.operationSequence - b.operationSequence);
  const batchSize = Number(route?.standardBatchSize) || 1;

  if (error) {
    return <Container sx={{ mt: 4 }}><Alert severity="error">{error}</Alert></Container>;
  }
  if (!wo) {
    return <Container sx={{ mt: 4, textAlign: 'center' }}><CircularProgress /></Container>;
  }

  const t = cost?.totals;

  return (
    <Container maxWidth={false} sx={{ mb: 4 }}>
      <PageHeader
        section="Production"
        title={wo.workOrderNumber}
        subtitle={`${wo.itemCode} — ${wo.itemDescription || ''} · lot ${wo.lotNumber || '—'}${wo.customerOrderId ? ` · order ${wo.customerOrderId}` : ''}`}
        actions={
          <>
            <Button startIcon={<Refresh />} variant="outlined" onClick={load}>Refresh</Button>
            <Button startIcon={<Print />} variant="outlined"
              onClick={() => window.open(`${API_BASE}/work-orders/${id}/report`, '_blank')}>
              Job Card
            </Button>
            <Button startIcon={<RequestQuote />} variant="outlined"
              onClick={() => window.open(`${API_BASE}/work-orders/${id}/cost-report/print`, '_blank')}>
              Cost Report
            </Button>
            <Button startIcon={<Warehouse />} variant="contained" disabled={busy === 'mr'}
              sx={{ bgcolor: '#f2b32c', color: '#1a1a1a', fontWeight: 700, '&:hover': { bgcolor: '#e0a41f' } }}
              onClick={requestMaterial}>
              Request Material
            </Button>
          </>
        }
      />

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card><CardContent>
            <Typography variant="subtitle2" color="text.secondary">Status & Quantities</Typography>
            <Box display="flex" alignItems="center" gap={1} my={1}>{chip(wo.status)}</Box>
            <Typography variant="body2">Planned <strong>{num(wo.quantityPlanned)}</strong> · Completed{' '}
              <strong>{num(wo.quantityCompleted)}</strong> · Scrapped <strong>{num(wo.quantityScrapped)}</strong> {wo.unitOfMeasure}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={8}>
          <Card><CardContent>
            <Typography variant="subtitle2" color="text.secondary">Costing (standard vs actual)</Typography>
            {t ? (
              <Grid container spacing={2} mt={0.25}>
                <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Standard material</Typography><Typography fontWeight={700}>{num(t.standardMaterialCost)}</Typography></Grid>
                <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Actual material</Typography><Typography fontWeight={700}>{num(t.actualMaterialCost)}</Typography></Grid>
                <Grid item xs={6} sm={2}><Typography variant="caption" color="text.secondary">Scrap + rework</Typography><Typography fontWeight={700}>{num(t.scrapCost + t.reworkCost)}</Typography></Grid>
                <Grid item xs={6} sm={2}><Typography variant="caption" color="text.secondary">Total actual</Typography><Typography fontWeight={700}>{num(t.totalActualCost)}</Typography></Grid>
                <Grid item xs={6} sm={2}><Typography variant="caption" color="text.secondary">Per good unit</Typography><Typography fontWeight={700} color={t.costPerGoodUnit ? 'primary' : 'text.secondary'}>{num(t.costPerGoodUnit)}</Typography></Grid>
              </Grid>
            ) : <Typography variant="body2" color="text.secondary">No cost data yet.</Typography>}
          </CardContent></Card>
        </Grid>
      </Grid>

      {/* Stages with per-stage inputs/outputs */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: 1 }}>
          <Typography variant="h6" gutterBottom>Process Stages — Material Flow</Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Expand a stage to see its inputs (from stores or a previous stage) and outputs
            (primary, co-products, by-products) per standard batch of {num(batchSize)}.
          </Typography>
        </CardContent>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={40} />
                <TableCell>Seq</TableCell>
                <TableCell>Stage</TableCell>
                <TableCell>Work center</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Done</TableCell>
                <TableCell align="right">Scrap</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {operations.map((op: any) => {
                const routeOp = routeOpBySeq.get(op.operationSequence);
                const open = !!openStages[op.id];
                return (
                  <React.Fragment key={op.id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton size="small" onClick={() => setOpenStages((s) => ({ ...s, [op.id]: !open }))}>
                          {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{op.operationSequence}</TableCell>
                      <TableCell><strong>{op.operationCode}</strong> {routeOp?.operationName || ''}</TableCell>
                      <TableCell>{op.workCenterCode || '—'}</TableCell>
                      <TableCell>{chip(op.status)}</TableCell>
                      <TableCell align="right">{num(op.quantityCompleted)}</TableCell>
                      <TableCell align="right">{num(op.quantityScrapped)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                          <Grid container spacing={2} sx={{ my: 0.5, pl: 6, pr: 2 }}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom>
                                <ArrowDownward sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                Inputs
                              </Typography>
                              {(routeOp?.inputs || []).length === 0 ? (
                                <Typography variant="body2" color="text.secondary">None routed</Typography>
                              ) : (routeOp.inputs.map((input: any) => (
                                <Box key={input.id} display="flex" gap={1} alignItems="center" mb={0.5}>
                                  <Chip size="small" variant="outlined"
                                    icon={input.sourceType === 'prior_operation' ? <ArrowDownward sx={{ fontSize: 12 }} /> : <Inventory2 sx={{ fontSize: 12 }} />}
                                    label={input.sourceType === 'prior_operation'
                                      ? `← stage ${input.sourceOperationSequence}` : 'stores'} />
                                  <Typography variant="body2">
                                    <strong>{input.productCode}</strong> {input.productName} — {num(input.quantityPer)} {input.uom}
                                  </Typography>
                                </Box>
                              )))}
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom>
                                <LocalShipping sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                Outputs
                              </Typography>
                              {(routeOp?.outputs || []).length === 0 ? (
                                <Typography variant="body2" color="text.secondary">None routed</Typography>
                              ) : (routeOp.outputs.map((output: any) => (
                                <Box key={output.id} display="flex" gap={1} alignItems="center" mb={0.5}>
                                  <Chip size="small"
                                    color={output.outputType === 'primary' ? 'success'
                                      : output.outputType === 'byproduct' ? 'warning'
                                      : output.outputType === 'coproduct' ? 'info' : 'default'}
                                    label={String(output.outputType || '').toUpperCase()} />
                                  <Typography variant="body2">
                                    <strong>{output.productCode}</strong> {output.productName} — {num(output.quantityPer)} {output.uom}
                                    {output.feedsNextOperation && output.nextOperationSequence
                                      ? ` → stage ${output.nextOperationSequence}` : ''}
                                    {output.requiresLotTracking ? ` · lot ${output.lotPrefix || ''}` : ''}
                                  </Typography>
                                </Box>
                              )))}
                            </Grid>
                          </Grid>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Materials reconciliation */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: 1 }}>
          <Typography variant="h6">Materials — Planned vs Requested vs Received vs Consumed</Typography>
        </CardContent>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell><TableCell>UoM</TableCell>
                <TableCell align="right">Planned</TableCell>
                <TableCell align="right">Requested</TableCell>
                <TableCell align="right">Received</TableCell>
                <TableCell align="right">Consumed</TableCell>
                <TableCell align="right">Available</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {materials.length === 0 ? (
                <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">No routed materials.</Typography></TableCell></TableRow>
              ) : materials.map((m) => (
                <TableRow key={m.itemCode} hover>
                  <TableCell><strong>{m.itemCode}</strong> <Typography variant="caption" color="text.secondary">{m.itemName}</Typography></TableCell>
                  <TableCell>{m.uom}</TableCell>
                  <TableCell align="right">{num(m.plannedQty)}</TableCell>
                  <TableCell align="right">{num(m.requestedQty)}</TableCell>
                  <TableCell align="right">{num(m.receivedQty)}</TableCell>
                  <TableCell align="right">{num(m.consumedQty)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={700}
                      color={m.availableQty < 0 ? 'error.main' : m.availableQty > 0 ? 'success.main' : 'text.primary'}>
                      {num(m.availableQty)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Material requests */}
      <Card>
        <CardContent sx={{ pb: 1 }}>
          <Typography variant="h6">Material Requests</Typography>
        </CardContent>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>MR No.</TableCell><TableCell>Status</TableCell>
                <TableCell>Warehouse</TableCell><TableCell>Outbound ref</TableCell>
                <TableCell align="right">Lines</TableCell><TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mrs.length === 0 ? (
                <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">None yet — use “Request Material”.</Typography></TableCell></TableRow>
              ) : mrs.map((mr) => (
                <TableRow key={mr.id} hover>
                  <TableCell><strong>{mr.mrNumber}</strong></TableCell>
                  <TableCell>{chip(mr.status)}</TableCell>
                  <TableCell>{mr.warehouseName || '—'}</TableCell>
                  <TableCell>{mr.referenceCode || '—'}</TableCell>
                  <TableCell align="right">{(mr.lines || []).length}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" title="Print issue slip"
                      onClick={() => window.open(`${API_BASE}/work-orders/material-requests/${mr.id}/report`, '_blank')}>
                      <Print fontSize="small" />
                    </IconButton>
                    {['requested', 'waiting_for_stock'].includes(mr.status) && (
                      <Button size="small" disabled={busy === mr.id + 'issue'} onClick={() => mrAction(mr.id, 'issue')}>Issue</Button>
                    )}
                    {mr.status === 'issued' && (
                      <Button size="small" color="success" disabled={busy === mr.id + 'receive'} onClick={() => mrAction(mr.id, 'receive')}>Receive</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
};

export default WorkOrderDetail;
